import { Log } from '@sortlist-frontend/mlm';
import { isBrowser } from '@sortlist-frontend/utils';
import Head from 'next/head';
import type {
  AggregateRating,
  Brand,
  ContactPoint,
  Organization,
  PostalAddress,
  Thing,
  WebPage,
  WebSite,
} from 'schema-dts';

import { usePublicAppContext } from '_core/context/public-app-context';

import { escapeJsonLd } from './utils';

const SCHEMA_CONTEXT = 'https://schema.org';

export const generateOrganization = ({
  origin,
  withRating = true,
}: {
  origin: string;
  withRating?: boolean;
}): Organization => {
  const logoUrl = `${origin}/_img/logos/sortlist/logotype-primary-negative.svg`;

  // Contact points for different regions
  const contactPoints: ContactPoint[] = [
    {
      '@type': 'ContactPoint',
      telephone: '+32 2 808 14 22',
      contactType: 'customer service',
      areaServed: 'BE',
      availableLanguage: ['French', 'English', 'Dutch'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+33 7 56 79 82 23',
      contactType: 'customer service',
      areaServed: 'FR',
      availableLanguage: ['French', 'English'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+34 91 198 96 86',
      contactType: 'customer service',
      areaServed: 'ES',
      availableLanguage: ['French', 'English', 'Spanish'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+31 10 808 0733',
      contactType: 'customer service',
      areaServed: 'NL',
      availableLanguage: ['French', 'English', 'Dutch'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+49 89 1208 5167',
      contactType: 'customer service',
      areaServed: 'DE',
      availableLanguage: ['German', 'English'],
    },
  ];

  // Organization address
  const address: PostalAddress = {
    '@type': 'PostalAddress',
    addressLocality: 'Wavre',
    postalCode: '1301',
    addressCountry: 'BE',
    streetAddress: '29 Avenue Zénobe Gramme',
  };

  // Aggregate rating for the organization
  const aggregateRating: AggregateRating = {
    '@type': 'AggregateRating',
    ratingValue: 3.7,
    reviewCount: 823,
    bestRating: 5,
  };

  // Organization
  return {
    '@type': 'Organization',
    '@id': `https://www.sortlist.com/#organization`,
    name: 'Sortlist',
    legalName: 'Sortlist SA',
    logo: {
      '@type': 'ImageObject',
      '@id': `https://www.sortlist.com/#logo`,
      url: logoUrl,
    },
    url: origin,
    sameAs: [
      'https://www.linkedin.com/company/s0rtli-st/',
      'https://twitter.com/sortlist',
      'https://www.facebook.com/wearesortlist/',
      'https://www.instagram.com/sortlist/',
      'https://www.youtube.com/sortlist',
      'https://www.crunchbase.com/organization/sortlist-www-sortlist-com',
    ],
    address,
    contactPoint: contactPoints,
    aggregateRating: withRating ? aggregateRating : undefined,
  };
};

export const generateWebsite = ({ origin, inLanguage }: { origin: string; inLanguage: string }): WebSite => ({
  '@type': 'WebSite',
  '@id': `${origin}#website`,
  url: origin,
  inLanguage,
  name: 'Sortlist',
  publisher: { '@id': 'https://www.sortlist.com/#organization' },
});

/**
 * Homepage JSON-LD structured data component
 * Generates a single script tag with all structured data for the homepage
 */
export const HomepageJsonld = ({ isHub = false }: { isHub?: boolean }) => {
  const { domainInfo, locale } = usePublicAppContext();
  const origin = domainInfo?.getOriginUrl() ?? '';
  const localeIso = domainInfo?.getIso31661();
  const logoUrl = `${origin}/_img/logos/sortlist/logotype-primary-negative.svg`;

  const inLanguage = localeIso != null ? `${locale}-${localeIso}` : locale;

  const organization = generateOrganization({ origin });

  const website = generateWebsite({ origin, inLanguage });

  const webpage: WebPage = {
    '@type': 'WebPage',
    '@id': `${origin}#webpage`,
    description: isHub
      ? 'Get steady qualified leads with buyer-intent signals, marketplace visibility and sales opportunities. Built for digital agencies.'
      : 'Discover the most relevant agencies for your project based on your own specific requirements.',
    lastReviewed: isHub ? '2026-09-02T00:00:00.000Z' : '2021-05-26T05:59:02.085Z',
    inLanguage,
    reviewedBy: {
      '@type': 'Organization',
      name: 'Sortlist',
    },
  };

  const brand: Brand = {
    '@type': 'Brand',
    '@id': `${origin}#brand`,
    slogan: isHub ? 'Lead generation and growth for agencies' : 'Find the right agency!',
    logo: logoUrl,
  };

  // Build the complete schema graph
  const schema: (WebSite | Organization | WebPage | Brand)[] = [website, organization, webpage, brand];

  const graph: { '@context': string; '@graph': Thing[] } = {
    '@context': SCHEMA_CONTEXT,
    '@graph': schema,
  };

  // Validate schema.org properties server-side only
  if (!isBrowser()) {
    import('./utils').then(({ validateSchemaOrg }) => {
      const errors = validateSchemaOrg(schema);
      if (errors.length > 0) {
        // We don't want to spam sentry issues with these but we still want to monitor the amount of logs
        // eslint-disable-next-line
        Log.error(`Schema.org validation errors for Homepage`, {
          errors: errors.map((err) => ({ type: err.type, path: err.path, message: err.message })),
        });
      }
    });
  }

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: escapeJsonLd(JSON.stringify(graph)) }} />
    </Head>
  );
};
